import React, { useEffect, useMemo, useState } from 'react';

export default function JournalCitationMetrics({ journalId, indicatorData = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null); // 'jcr' | 'scopus' | 'kci'

  useEffect(() => {
    let alive = true;

    // Use injected indicatorData only; do not fetch here.
    if (indicatorData) {
      setData(indicatorData);
      const hasJcr = Array.isArray(indicatorData?.jcr_index) && indicatorData.jcr_index.length > 0;
      const hasScopus = Array.isArray(indicatorData?.scopus_index) && indicatorData.scopus_index.length > 0;
      const hasKci = Array.isArray(indicatorData?.kci_index) && indicatorData.kci_index.length > 0;
      const first = hasJcr ? 'jcr' : hasScopus ? 'scopus' : hasKci ? 'kci' : null;
      setActive(first);
      setLoading(false);
      setError(null);
    } else {
      // No data provided; clear state
      setData(null);
      setActive(null);
      setLoading(false);
      setError(null);
    }

    return () => { alive = false; };
  }, [indicatorData]);

  const tabs = useMemo(() => {
    if (!data) return [];
    const t = [];
    if (Array.isArray(data?.jcr_index) && data.jcr_index.length > 0) t.push({ key: 'jcr', label: 'JCR' });
    if (Array.isArray(data?.scopus_index) && data.scopus_index.length > 0) t.push({ key: 'scopus', label: 'Scopus' });
    if (Array.isArray(data?.kci_index) && data.kci_index.length > 0) t.push({ key: 'kci', label: 'KCI' });
    return t;
  }, [data]);

  // Helpers
  const sortByYearDesc = (rows = []) =>
    [...rows].sort((a, b) => (b?.indicator_year || 0) - (a?.indicator_year || 0));

  // Generic table renderer used by JCR, Scopus, and KCI
  function renderMetricsTable(columns = [], rows = []) {
    // if no rows, show friendly message
    if (!Array.isArray(rows) || rows.length === 0) {
      return <div className="text-[14px] text-[#6C7687]">No metrics available.</div>;
    }

    const formatNumberWithCommas = (value) => {
      if (value === null || value === undefined || value === '') return '';
      const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
      if (!Number.isFinite(n)) return String(value); // 숫자로 못 바꾸면 원문 표시
      return n.toLocaleString();
    };

    const formatCellValue = (colKey, value) => {
      if (colKey === 'total_cites' || colKey === 'citation_count') return formatNumberWithCommas(value);
      return value ?? '';
    };

    const getColStyle = (colKey) => {
      const base = { paddingLeft: '6px', paddingRight: '6px' };
      if (colKey === 'indicator_year') {
        return {
          ...base,
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          whiteSpace: 'nowrap',
        };
      }
      return base;
    };

    const sorted = sortByYearDesc(rows);
    return (
      <div className="overflow-x-auto">
        <table className="min-w-[800px]">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key} style={getColStyle(col.key)}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, idx) => (
              <tr key={idx}>
                {columns.map(col => (
                  <td key={col.key} style={{ paddingLeft: '6px', paddingRight: '6px' }}>{formatCellValue(col.key, r?.[col.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function getColumnsFor(activeKey, sampleRows = []) {
    if (activeKey === 'jcr') {
      return [
        { key: 'indicator_year', label: 'Year' },
        { key: 'jif', label: 'JIF' },
        { key: 'impact_5', label: '5 year JIF' },
        { key: 'immediacy', label: 'Immediacy Index' },
        { key: 'article_influence', label: 'Article Influence Score' },
        { key: 'eigen_factor', label: 'Eigenfactor Score' },
      ];
    }
    if (activeKey === 'scopus') {
      // Required order per spec:
      // Year, Percent Cited, Cite Score, Citation Count, Scholarly Output, SJR, Top 10%, SNIP
      return [
        { key: 'indicator_year', label: 'Year' },
        { key: 'cite_score', label: 'Cite Score' },
        { key: 'sjr', label: 'SJR' },
        { key: 'snip', label: 'SNIP' },
        { key: 'citation_count', label: 'Citation Count' },
        { key: 'scholarly_output', label: 'Scholarly Output' },
      ];
    }
    if (activeKey === 'kci') {
      // Explicit KCI columns and Korean labels per spec
      return [
        { key: 'indicator_year', label: 'Year' },
        { key: 'impact', label: 'KCI IF (2년)' },
        { key: 'impact_no_self', label: 'KCI IF (2년, 자기인용제외)' },
        { key: 'centrality', label: '중심성지수' },
        { key: 'immediacy', label: '즉시성지수' },
        { key: 'total_items', label: '논문수' },
        { key: 'total_cites', label: '피인용횟수' },
      ];
    }
    return [{ key: 'indicator_year', label: 'Year' }];
  }

  function renderTable() {
    if (!active) return null;

    const rows =
      active === 'jcr' ? (data?.jcr_index || []) :
      active === 'scopus' ? (data?.scopus_index || []) :
      active === 'kci' ? (data?.kci_index || []) : [];

    const columns = getColumnsFor(active, rows);
    return renderMetricsTable(columns, rows);
  }

  function renderAllTabsForPrint() {
    if (!data) return null;
    const allTabs = [];
    if (Array.isArray(data?.jcr_index) && data.jcr_index.length > 0) allTabs.push({ key: 'jcr', label: 'JCR', rows: data.jcr_index });
    if (Array.isArray(data?.scopus_index) && data.scopus_index.length > 0) allTabs.push({ key: 'scopus', label: 'Scopus', rows: data.scopus_index });
    if (Array.isArray(data?.kci_index) && data.kci_index.length > 0) allTabs.push({ key: 'kci', label: 'KCI', rows: data.kci_index });
    return allTabs.map(t => (
      <div key={t.key} className={`${t.key !== 'jcr' ? 'not_print ' : ''}mb-6`}>
        <h4 className="text-[16px] font-bold mb-3">{t.label}</h4>
        {renderMetricsTable(getColumnsFor(t.key, t.rows), t.rows)}
      </div>
    ));
  }

  return (
    <>
      {loading && !data && (
        <div className="text-[14px] text-[#6C7687]">Loading...</div>
      )}
      {error && (
        <div className="text-red-600 text-[14px]">{String(error)}</div>
      )}
      <div className="print-hide-tabs">
        {tabs.length > 0 && (
          <ul id="customOptions" className="not_print mb-4 p-[6px] bg-[#F3F5F9] rounded-lg z-10 text-[#8D9AAF] inline-flex text-[15px] gap-[5px]">
            {tabs.map(t => (
              <li
                key={t.key}
                className={(active === t.key ? 'active ' : '') + 'w-[104px] text-center leading-[38px] hover:bg-white rounded-lg cursor-pointer'}
                onClick={() => setActive(t.key)}
                data-value={t.key}
              >
                {t.label}
              </li>
            ))}
          </ul>
        )}
        {tabs.length === 0 && !loading && (
          <div className="text-[14px] text-[#6C7687]">No citation metrics available.</div>
        )}
        {renderTable()}
      </div>
      <div className="print-all-tabs" style={{ display: 'none' }}>
        {renderAllTabsForPrint()}
      </div>
    </>
  );
}
